import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doFetch } from "storybook-dashboard/utils/fetching";
import { Link } from "react-router-dom";
import { Hide, nestByDunderKeys } from "storybook-dashboard/utils";
import CompanyTree from "AppSrc/tree";
import { getRoleName } from "./utils";
import Im from "immutable";
import Octicon from "react-octicon";
import { GlobalToggleButton } from "AppSrc/components/GlobalToggleButton";
import { Tooltip } from "react-tippy";
import { Modal, useModal } from "storybook-dashboard/components/modal";

// Standalone function for removing user from all roles
const handleRemoveUserFromAll = (userId, companyId, trigger) => {
  console.log(`User ${userId} will be removed from all roles in company ${companyId}`);
  doFetch(`/api/company/${companyId}/dispatch/`, "POST", {
    type: "DELETE_USER_FROM_ALL",
    payload: { user_id: userId },
  }).then(() => {
    trigger();
  });
};

// Remove User from All Confirmation Modal Component
function RemoveUserFromAllModal({ onConfirm, userName }) {
  return (
    <Modal>
      <div className="modal-header">
        <h5 className="modal-title text-danger">
          <Octicon name="alert" style={{ marginRight: "8px" }} />
          Remove User from All Roles
        </h5>
      </div>
      <div className="modal-body">
        <div className="alert alert-warning">
          <div className="d-flex align-items-center">
            <Octicon name="alert" style={{ marginRight: "12px", fontSize: "16px" }} />
            <div>
              <strong>Warning: This action cannot be undone.</strong>
            </div>
          </div>
        </div>
        <p>
          Are you sure you want to remove <strong>{userName || "this user"}</strong> from all roles and permissions
          across all organizational levels?
        </p>
        <p className="text-muted small mb-0">This will remove the user from:</p>
        <ul className="text-muted small mt-2">
          <li>All Admin roles</li>
          <li>All Viewer roles</li>
          <li>All Project-specific roles</li>
          <li>Any custom role assignments</li>
        </ul>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" data-dismiss="modal">
          Cancel
        </button>
        <button type="button" className="btn btn-danger" onClick={onConfirm} data-dismiss="modal">
          <Octicon name="person-fill" style={{ marginRight: "6px" }} />
          Remove from All
        </button>
      </div>
    </Modal>
  );
}

// Add CSS styles for row hover effects
const hoverStyles = `
  <style>
    .role-management-table tr:hover .role-row-cell {
      background-color: #f8f9fa !important;
      box-shadow: inset 0 0 0 1px #dee2e6;
    }
    
    .role-management-table tr:hover .role-row-cell:first-child {
      border-left: 3px solid #007bff !important;
    }
    
    .role-management-table .role-row-cell {
      transition: all 0.15s ease-in-out;
    }
    
    /* Preserve the pending changes styling even on hover */
    .role-management-table tr:hover .role-row-cell.has-changes {
      background-color: #fff3cd !important;
      border-left: 3px solid #ffc107 !important;
    }
  </style>
`;

const isChecked = ({ dashboardId, roleName, rolesByName, updates }) => {
  // Check for individual dashboard updates first (they override wildcards)
  if (updates.hasIn([dashboardId, roleName])) {
    return updates.getIn([dashboardId, roleName]);
  }

  // Handle wildcard "select/deselect all" cases
  if (updates.hasIn(["*", roleName])) {
    return updates.getIn(["*", roleName]);
  }

  // Default to original role state
  return rolesByName.has(roleName);
};

const onCheckChange = (e, { dashboardId, roleName, rolesByName, updates, setUpdates }) => {
  let newVal = e.target.checked;
  const orgVal = rolesByName.has(roleName);

  // If the new value matches the original database value AND there's no wildcard override, remove the update
  const shouldDelete = newVal === orgVal && !updates.hasIn(["*", roleName]);

  if (shouldDelete) {
    setUpdates(updates.deleteIn([dashboardId, roleName]));
  } else {
    // Always set the individual override when user clicks after a wildcard
    setUpdates(updates.setIn([dashboardId, roleName], newVal));
  }
};

function RoleCheckbox(props) {
  const { dashboardId, roleName, rolesByName, updates } = props;
  const checked = isChecked(props);
  const originalValue = rolesByName.has(roleName);
  const hasChanged = updates.hasIn([dashboardId, roleName]) || updates.hasIn(["*", roleName]);
  const onChange = (e) => onCheckChange(e, props);

  return (
    <div className="form-group form-check d-flex align-items-center justify-content-center">
      <div className="position-relative">
        <input
          type="checkbox"
          className="form-check-input"
          checked={checked}
          onChange={onChange}
          style={{
            transform: "scale(1.2)",
            accentColor: checked ? "#28a745" : "#6c757d",
          }}
        />
        {hasChanged && (
          <div
            className="position-absolute"
            style={{
              top: "-3px",
              right: "-3px",
              width: "8px",
              height: "8px",
              backgroundColor: "#ffc107",
              borderRadius: "50%",
              border: "1px solid white",
              boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
            }}
            title={`Changed from ${originalValue ? "checked" : "unchecked"} to ${checked ? "checked" : "unchecked"}`}
          />
        )}
      </div>
    </div>
  );
}

function ProjectDisciplineCell({ dashboardId, roles }) {
  const _dashboardId = (dashboardId || "").substring(0, 8);
  const linkUrl = `/project/${_dashboardId}/members`;

  if (!roles || roles.size === 0) {
    return (
      <div className="d-flex flex-wrap justify-content-center" style={{ maxWidth: "200px" }}>
        <Link
          to={linkUrl}
          className="badge bg-secondary text-white small mb-1"
          style={{ textDecoration: "none", fontSize: "10px", padding: "4px 6px" }}
          title="Manage project roles"
        >
          <Octicon name="link-external" className="me-1" style={{ fontSize: "8px" }} />
          No project roles assigned
        </Link>
      </div>
    );
  }

  return (
    <div className="d-flex flex-wrap justify-content-center" style={{ maxWidth: "200px" }}>
      {roles.map((r, i) => (
        <Link
          key={i}
          to={linkUrl}
          className="badge bg-primary text-white small mr-1 mb-1"
          style={{
            textDecoration: "none",
            fontSize: "10px",
            padding: "4px 6px",
            minWidth: "fit-content",
          }}
          title={`Manage roles for ${r?.getIn(["project_discipline", "name"])}`}
        >
          <Octicon name="link-external" className="mr-1" style={{ fontSize: "8px" }} />
          {r?.getIn(["project_discipline", "name"])}
        </Link>
      ))}
    </div>
  );
}

function TableCellContent(props) {
  const { roleName } = props;
  const CellComponent = roleName === "Project Roles" ? ProjectDisciplineCell : RoleCheckbox;
  return <CellComponent {...props} />;
}

const cellStyles = { verticalAlign: "middle", border: "none", textAlign: "center" };

const getDashboardRoles = ({ dashboardId, userRolesByDashboardId }) => {
  const roles = userRolesByDashboardId?.get(dashboardId) || Im.List();
  const rolesByName = roles.reduce((acc, r) => acc.set(getRoleName(r), r), Im.Map());
  return { roles, rolesByName };
};

function RoleColumns(props) {
  const { companyId, projectId, roleNames, userRolesByDashboardId, updates } = props;
  let excludeRoles = companyId ? Im.Set(["Project Roles", "Reporter", "Approver"]) : Im.Set();

  const dashboardId = companyId || projectId;
  const { roles, rolesByName } = getDashboardRoles({ dashboardId, userRolesByDashboardId });

  // Check if this row has any pending changes
  const hasRowChanges = roleNames.some(
    (roleName) => updates.hasIn([dashboardId, roleName]) || updates.hasIn(["*", roleName])
  );

  const rowStyles = {
    backgroundColor: hasRowChanges ? "#fff3cd" : "transparent",
    transition: "background-color 0.2s ease-in-out",
    cursor: "pointer",
  };

  return (
    <>
      {roleNames.map((roleName, index) => {
        // Add borders for Project Roles column to show row boundaries
        const projectRolesBorders =
          roleName === "Project Roles"
            ? {
                borderTop: "1px solid #dee2e6",
                borderBottom: "1px solid #dee2e6",
              }
            : {};

        return (
          <td
            key={roleName}
            className={`role-row-cell ${hasRowChanges ? "has-changes" : ""}`}
            style={{
              ...cellStyles,
              ...(roleName == "Project Roles" ? { textAlign: "left" } : {}),
              ...rowStyles,
              ...projectRolesBorders,
              borderLeft: index === 0 && hasRowChanges ? "3px solid #ffc107" : "none",
            }}
            title={`Manage ${roleName} permissions for this level`}
          >
            <Hide when={excludeRoles.has(roleName)}>
              <TableCellContent {...props} {...{ dashboardId, roleName, roles, rolesByName }} />
            </Hide>
          </td>
        );
      })}
    </>
  );
}

function TriStateHeaderCheckbox({ roleName, onSelectAll, onDeselectAll, userRolesByDashboardId, updates }) {
  // Calculate the state of all checkboxes for this role across all dashboards
  const getColumnState = () => {
    const dashboards = userRolesByDashboardId?.keySeq()?.toArray();
    let checkedCount = 0;
    let uncheckedCount = 0;

    // Check if there's a wildcard update
    const wildcardUpdate = updates?.getIn(["*", roleName]);
    if (wildcardUpdate !== undefined) {
      return wildcardUpdate ? "all" : "none";
    }

    dashboards?.forEach((dashboardId) => {
      const { rolesByName } = getDashboardRoles({ dashboardId, userRolesByDashboardId });
      const isCurrentlyChecked = isChecked({ dashboardId, roleName, rolesByName, updates });

      if (isCurrentlyChecked) {
        checkedCount++;
      } else {
        uncheckedCount++;
      }
    });

    // If no dashboards at all, return none
    if (checkedCount === 0 && uncheckedCount === 0) return "none";

    // If all are checked, return all
    if (checkedCount > 0 && uncheckedCount === 0) return "all";

    // If none are checked, return none
    if (checkedCount === 0 && uncheckedCount > 0) return "none";

    // If mix of checked and unchecked, return some
    return "some";
  };

  const state = getColumnState();

  const handleToggle = () => {
    // If all or some are checked, deselect all. If none are checked, select all.
    if (state === "all" || state === "some") {
      onDeselectAll(roleName);
    } else {
      onSelectAll(roleName);
    }
  };

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-start"
      style={{ height: "100%", width: "100%", paddingTop: "4px" }}
    >
      <div
        className="d-flex align-items-center justify-content-center mb-2"
        title={
          state === "all"
            ? `All ${roleName} roles selected - click to deselect all`
            : state === "some"
            ? `Some ${roleName} roles selected - click to deselect all`
            : `No ${roleName} roles selected - click to select all`
        }
        style={{
          cursor: "pointer",
          padding: "4px",
          borderRadius: "4px",
          backgroundColor: state !== "none" ? "#e3f2fd" : "transparent",
          transition: "all 0.15s ease-in-out",
          border: "1px solid " + (state !== "none" ? "#90caf9" : "transparent"),
        }}
        onClick={handleToggle}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = state !== "none" ? "#bbdefb" : "#f8f9fa";
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = state !== "none" ? "#e3f2fd" : "transparent";
        }}
      >
        <input
          type="checkbox"
          className="form-check-input"
          checked={state === "all"}
          ref={(checkbox) => {
            if (checkbox) {
              checkbox.indeterminate = state === "some";
            }
          }}
          onChange={handleToggle}
          style={{
            cursor: "pointer",
            transform: "scale(1.1)",
            accentColor: state === "some" ? "#ff9800" : "#007bff",
            margin: 0,
          }}
        />
      </div>
      <div className="d-flex flex-column align-items-center text-center">
        <div style={{ fontSize: "12px", fontWeight: "600", lineHeight: "1.2", marginBottom: "2px" }}>{roleName}</div>
        <Tooltip
          html={<>Select all will apply changes to all levels including any nested levels which may not be visible</>}
        >
          <div style={{ fontSize: "9px", color: "#6c757d", lineHeight: "1.1" }}>
            <Octicon style={{ fontSize: "11px" }} name="info" /> {roleName === "Admin" ? "Full Access" : "View Only"}
          </div>
        </Tooltip>
      </div>
    </div>
  );
}

function TableHeaderComponent(props) {
  const { roleNames } = props;
  const width = 60 / roleNames.size;
  const cellStyles = {
    border: "none",
    width: `${width}%`,
    textAlign: "center",
    verticalAlign: "middle",
    backgroundColor: "#f8f9fa",
    borderBottom: "2px solid #dee2e6",
    padding: "12px 8px",
    height: "70px",
  };

  const getRoleDescription = (roleName) => {
    switch (roleName) {
      case "Admin":
        return "Full access to manage dashboards, users, and settings";
      case "Viewer":
        return "Read-only access to view dashboard content";
      case "Project Roles":
        return "Specific project-level role assignments - click to manage";
      default:
        return `${roleName} role permissions`;
    }
  };

  return (
    <thead>
      <tr>
        <th
          style={{
            border: "none",
            width: "40%",
            verticalAlign: "middle",
            backgroundColor: "#f8f9fa",
            borderBottom: "2px solid #dee2e6",
            padding: "12px 16px",
            height: "70px",
          }}
        >
          <div className="d-flex flex-row mb-2">
            <strong className="mr-2">Dashboard Hierarchy</strong>
            <GlobalToggleButton iconOnly={true} />
          </div>
          <small style={{ fontSize: "11px", color: "#6c757d", display: "flex", alignItems: "center" }}>
            <Octicon name="info" style={{ marginRight: "4px", fontSize: "11px" }} />
            Organisational structure from company level down to individual projects
          </small>
        </th>
        {roleNames.map((roleName) => (
          <th
            key={roleName}
            style={{ ...cellStyles, ...(roleName == "Project Roles" ? { textAlign: "left" } : {}) }}
            title={getRoleDescription(roleName)}
          >
            {roleName == "Project Roles" ? (
              <div className="d-flex flex-column align-items-center justify-content-center text-center h-100">
                <div className="d-flex align-items-center justify-content-center mb-1">
                  <Octicon name="link-external" style={{ fontSize: "14px", color: "#6c757d" }} />
                </div>
                <div>
                  <div style={{ fontSize: "12px", fontWeight: "600", lineHeight: "1.2", marginBottom: "2px" }}>
                    Project Roles
                  </div>
                  <div style={{ fontSize: "9px", color: "#6c757d", lineHeight: "1.1" }}>Click to Manage</div>
                </div>
              </div>
            ) : (
              <TriStateHeaderCheckbox {...props} {...{ roleName }} />
            )}
          </th>
        ))}
      </tr>
    </thead>
  );
}

const onSubmit = (companyId, userId, updates, setUpdates, trigger, setIsSaving) => {
  console.log("Updating roles below dashboard", companyId, " for user ", userId, updates?.toJS());

  setIsSaving(true);
  doFetch(`/api/company/${companyId}/user/roles/${userId}/`, "PUT", updates?.toJS())
    .then(() => {
      setUpdates(Im.Map());
      if (trigger) trigger();
      // Show success feedback
      const successElement = document.createElement("div");
      successElement.className = "alert alert-success alert-dismissible fade show position-fixed";
      successElement.style.cssText = "top: 20px; right: 20px; z-index: 1060; min-width: 300px;";
      successElement.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="fas fa-check-circle mr-2"></i>
          Role changes saved successfully!
        </div>
        <button type="button" class="close" onclick="this.parentElement.remove()">
          <span>&times;</span>
        </button>
      `;
      document.body.appendChild(successElement);
      setTimeout(() => successElement.remove(), 4000);
    })
    .catch((error) => {
      console.error("Error saving roles:", error);
      // Show error feedback
      const errorElement = document.createElement("div");
      errorElement.className = "alert alert-danger alert-dismissible fade show position-fixed";
      errorElement.style.cssText = "top: 20px; right: 20px; z-index: 1060; min-width: 300px;";
      errorElement.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Failed to save changes. Please try again.
        </div>
        <button type="button" class="close" onclick="this.parentElement.remove()">
          <span>&times;</span>
        </button>
      `;
      document.body.appendChild(errorElement);
      setTimeout(() => errorElement.remove(), 6000);
    })
    .finally(() => {
      setIsSaving(false);
    });
};

export const RoleTypesGuide = ({ isProject }) => {
  return (
    <div className="row">
      <div className="col-md-4">
        <h6 className="text-primary mb-2">
          <Octicon name="shield" style={{ marginRight: "6px" }} />
          Admin Role
        </h6>
        <p className="small text-muted mb-0">
          Full access to manage dashboards, users, settings, and all content within each level.
        </p>
      </div>
      <div className="col-md-4">
        <h6 className="text-info mb-2">
          <Octicon name="eye" style={{ marginRight: "6px" }} />
          Viewer Role
        </h6>
        <p className="small text-muted mb-0">
          Read-only access to view dashboard content, reports, and data without editing capabilities.
        </p>
      </div>
      <div className="col-md-4">
        <h6 className="text-success mb-2">
          <Octicon name="link-external" style={{ marginRight: "6px" }} />
          Project Roles
        </h6>
        <p className="small text-muted mb-0">
          Specific project-level roles.{" "}
          {!isProject
            ? "Click badges to manage detailed permissions for each project."
            : "Navigate to the user permissions tab to see the full list of project roles."}
        </p>
      </div>
    </div>
  );
};

function RoleManagementGuide() {
  return (
    <div className="card mb-4" style={{ border: "1px solid #e3f2fd", backgroundColor: "#fafcff" }}>
      <div className="card-body py-3">
        <RoleTypesGuide />
        <hr className="my-3" />
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <small className="text-muted">
              <strong>Tip:</strong> Use column headers to select/deselect all roles at once. Changes are highlighted in
              yellow.
            </small>
          </div>
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center mr-3">
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#ffc107",
                  borderRadius: "50%",
                  marginRight: "4px",
                }}
              ></div>
              <small className="text-muted">Pending changes</small>
            </div>
            <div className="d-flex align-items-center">
              <div style={{ width: "3px", height: "12px", backgroundColor: "#ffc107", marginRight: "4px" }}></div>
              <small className="text-muted">Modified row</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UserRoleTree({
  userRolesByDashboardId,
  userCompanyRoleNames,
  trigger,
  isLoading,
  onSaveWithPermissionCheck,
  userName, // Add userName prop to display in modal
}) {
  let { _companyId, _userId } = useParams();
  const [updates, setUpdates] = useState(Im.Map());
  const [isSaving, setIsSaving] = useState(false);
  const { setModal } = useModal(); // Add modal hook
  console.log("UserRoleTree", updates?.toJS());

  const setRoles = ["Admin", "Viewer"];
  const setRolesSet = Im.Set(setRoles);
  const roleNames = Im.List(setRoles)
    .concat(userCompanyRoleNames?.filter((i) => !setRolesSet?.has(i))?.toList())
    ?.push("Project Roles");

  // Function to handle "Select All" for a specific role
  const handleSelectAll = (roleName) => {
    setUpdates(updates.setIn(["*", roleName], true));
  };

  // Function to handle "Deselect All" for a specific role
  const handleDeselectAll = (roleName) => {
    setUpdates(updates.setIn(["*", roleName], false));
  };

  // Function to show remove user confirmation modal
  const showRemoveUserModal = () => {
    setModal(
      <RemoveUserFromAllModal
        onConfirm={() => handleRemoveUserFromAll(_userId, _companyId, trigger)}
        userName={userName}
      />
    );
  };

  // Calculate pending changes statistics
  const pendingChanges = updates.entrySeq().reduce((count, [key, value]) => {
    if (key === "*") {
      // Count wildcard changes
      return count + value.entrySeq().size;
    } else {
      // Count individual dashboard changes
      return count + (Im.isMap(value) ? value.entrySeq().size : 1);
    }
  }, 0);

  const hasChanges = pendingChanges > 0;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div className="mt-2 text-muted">Loading role assignments...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: hoverStyles }} />
      <RoleManagementGuide />

      <div className="card">
        <div className="card-body p-0 role-management-table">
          <CompanyTree
            fromHere={true}
            showGlobalToggle={false}
            ExtraContentComponent={(props) => (
              <RoleColumns {...props} {...{ roleNames, userRolesByDashboardId, updates, setUpdates }} />
            )}
            excludeLevels={Im.Set(["ReportingPackage"])}
            TableHeaderComponent={() => (
              <TableHeaderComponent
                roleNames={roleNames}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                userRolesByDashboardId={userRolesByDashboardId}
                updates={updates}
              />
            )}
          />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center">
          {hasChanges && (
            <div className="alert alert-warning d-flex align-items-center mb-0 py-2 px-3">
              <Octicon name="alert" style={{ marginRight: "8px", fontSize: "14px" }} />
              <span className="small">
                <strong>{pendingChanges}</strong> pending change{pendingChanges !== 1 ? "s" : ""} - remember to save
                your changes
              </span>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center">
          <button
            className="btn btn-outline-secondary mr-3"
            onClick={() => setUpdates(Im.Map())}
            disabled={!hasChanges || isSaving}
            title={hasChanges ? "Clear all pending changes" : "No changes to clear"}
          >
            <Octicon name="x" style={{ marginRight: "6px" }} />
            Clear Changes
            {hasChanges && <span className="badge badge-warning ml-2">{pendingChanges}</span>}
          </button>

          <button
            className="btn btn-primary"
            onClick={() => {
              if (onSaveWithPermissionCheck) {
                const originalSaveFunction = () => {
                  setIsSaving(true);
                  return doFetch(`/api/company/${_companyId}/user/roles/${_userId}/`, "PUT", updates?.toJS())
                    .then(() => {
                      setUpdates(Im.Map());
                      if (trigger) trigger();
                      // Show success feedback
                      const successElement = document.createElement("div");
                      successElement.className = "alert alert-success alert-dismissible fade show position-fixed";
                      successElement.style.cssText = "top: 20px; right: 20px; z-index: 1060; min-width: 300px;";
                      successElement.innerHTML = `
                        <div class="d-flex align-items-center">
                          <i class="fas fa-check-circle mr-2"></i>
                          Role changes saved successfully!
                        </div>
                        <button type="button" class="close" onclick="this.parentElement.remove()">
                          <span>&times;</span>
                        </button>
                      `;
                      document.body.appendChild(successElement);
                      setTimeout(() => successElement.remove(), 4000);
                    })
                    .catch((error) => {
                      console.error("Error saving roles:", error);
                      // Show error feedback
                      const errorElement = document.createElement("div");
                      errorElement.className = "alert alert-danger alert-dismissible fade show position-fixed";
                      errorElement.style.cssText = "top: 20px; right: 20px; z-index: 1060; min-width: 300px;";
                      errorElement.innerHTML = `
                        <div class="d-flex align-items-center">
                          <i class="fas fa-exclamation-triangle mr-2"></i>
                          Failed to save changes. Please try again.
                        </div>
                        <button type="button" class="close" onclick="this.parentElement.remove()">
                          <span>&times;</span>
                        </button>
                      `;
                      document.body.appendChild(errorElement);
                      setTimeout(() => errorElement.remove(), 6000);
                      throw error;
                    })
                    .finally(() => {
                      setIsSaving(false);
                    });
                };
                onSaveWithPermissionCheck(updates, originalSaveFunction);
              } else {
                onSubmit(_companyId, _userId, updates, setUpdates, trigger, setIsSaving);
              }
            }}
            disabled={!hasChanges || isSaving}
            title={hasChanges ? "Save all role changes" : "No changes to save"}
          >
            {isSaving ? (
              <>
                <div className="spinner-border spinner-border-sm mr-2" role="status">
                  <span className="sr-only">Saving...</span>
                </div>
                Saving...
              </>
            ) : (
              <>
                <Octicon name="check" style={{ marginRight: "6px" }} />
                Save Changes
                {hasChanges && <span className="badge badge-light ml-2">{pendingChanges}</span>}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-end mt-3">
        <button
          className="btn btn-outline-danger"
          onClick={showRemoveUserModal}
          disabled={isSaving}
          title="Remove user from all roles and permissions"
        >
          <Octicon name="person-fill" style={{ marginRight: "6px" }} />
          Remove User from All
        </button>
      </div>
    </>
  );
}
